import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { legacyAuditRedirect } from './features/compliance/audit/legacy-audit-redirect';

// Header title + eyebrow per design/features.md §9.
// Home (path '') intentionally has no header title — the page opens with
// the personal greeting, and duplicating it in chrome would feel ceremonial.
export const routes: Routes = [
  { path: 'login', loadComponent: () => import('./features/auth/login.component').then(m => m.LoginComponent) },
  // Anonymous by necessity: whoever forgot their password cannot authenticate.
  // The one-time token in the query string is the only credential.
  {
    path: 'reset-password',
    loadComponent: () => import('./features/auth/reset-password.component').then(m => m.ResetPasswordComponent)
  },
  {
    path: '',
    loadComponent: () => import('./shared/layout/app-shell.component').then(m => m.AppShellComponent),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./features/home/home.component').then(m => m.HomeComponent),
        data: { headerTitle: null, headerEyebrow: null }
      },
      {
        path: 'profile',
        loadComponent: () => import('./features/profile/profile.component').then(m => m.ProfileComponent),
        data: { headerTitle: 'Profile', headerEyebrow: null }
      },
      {
        path: 'change-password',
        loadComponent: () => import('./features/auth/change-password.component').then(m => m.ChangePasswordComponent),
        data: { headerTitle: 'Change password', headerEyebrow: 'Your account' }
      },
      {
        path: 'tenant-settings',
        loadComponent: () => import('./features/tenant-settings/tenant-settings.component').then(m => m.TenantSettingsComponent),
        data: { headerTitle: 'Authentication', headerEyebrow: 'Tenant administration' }
      },
      {
        path: 'api-tokens',
        loadComponent: () => import('./features/api-tokens/api-tokens.component').then(m => m.ApiTokensComponent),
        data: { headerTitle: 'API tokens', headerEyebrow: 'Tenant administration' }
      },
      // Audit lived at /environment/audit until 2026-10-09. Outside the Environment
      // guard on purpose (see legacy-audit-redirect.ts).
      { path: 'environment/audit', children: [{ path: '**', redirectTo: legacyAuditRedirect }] },
      {
        path: 'environment',
        loadChildren: () => import('./features/environment/environment.routes').then(m => m.environmentRoutes),
        data: { headerTitle: 'Environment Module', headerEyebrow: 'ESG Compliance' }
      },
      {
        path: 'compliance',
        loadChildren: () => import('./features/compliance/compliance.routes').then(m => m.complianceRoutes),
        data: { headerTitle: 'Compliance Module', headerEyebrow: 'ESG Compliance' }
      },
      {
        path: 'company-settings',
        loadChildren: () => import('./features/company-settings/company-settings.routes').then(m => m.routes),
        data: { headerTitle: 'Company Settings', headerEyebrow: 'Administration' }
      },
    ]
  },
  // An unknown address says so, rather than being redirected home.
  //
  // The redirect was there to spare anyone a blank page, and the price was that
  // the app could not tell a typo from a broken link — and neither could we.
  // /users, which the home page's Manage users button pointed at, has never
  // existed; the wildcard swallowed it and the button read as dead for months.
  //
  // Expect this to surface other links that were quietly going nowhere. They
  // were already broken; they were just answering 200.
  {
    path: '**',
    loadComponent: () => import('./features/not-found/not-found.component').then(m => m.NotFoundComponent),
    data: { headerTitle: 'Not found', headerEyebrow: '' }
  }
];
